import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, Search, Filter, ShoppingCart, Star } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import EstimateDialog from "@/components/EstimateDialog";
import SEOHelmet from "@/components/SEOHelmet";

const categories = [
  "All Products",
  "Luxury Furniture",
  "Storage Solutions",
  "Home Decor", 
  "Lighting",
  "Textiles",
  "Professional Tools"
];

const products = [
  // Luxury Furniture
  {
    id: 6,
    name: "Minimalist Oak Storage Cabinet",
    price: 1899,
    originalPrice: 2299,
    category: "Luxury Furniture",
    image: "/api/placeholder/400/400",
    rating: 4.9,
    reviews: 127,
    badge: "Designer Piece",
    description: "Handcrafted oak with hidden storage compartments"
  },
  {
    id: 7,
    name: "Italian Marble Console Table",
    price: 3299,
    category: "Luxury Furniture",
    image: "/api/placeholder/400/400",
    rating: 5.0,
    reviews: 89,
    badge: "Exclusive",
    description: "Carrara marble with brass inlay details"
  },
  {
    id: 8,
    name: "Japanese Zen Meditation Chair",
    price: 1599,
    category: "Luxury Furniture",
    image: "/api/placeholder/400/400",
    rating: 4.8,
    reviews: 156,
    description: "Handmade teak with organic curves for mindful living"
  },
  // Storage Solutions
  {
    id: 9,
    name: "Modular Closet System Pro",
    price: 1299,
    category: "Storage Solutions",
    image: "/api/placeholder/400/400",
    rating: 4.9,
    reviews: 234,
    badge: "Professional Grade",
    description: "Customizable closet solution used by our organizers"
  },
  {
    id: 10,
    name: "Luxury Jewelry Organization Tower",
    price: 449,
    category: "Storage Solutions",
    image: "/api/placeholder/400/400",
    rating: 4.8,
    reviews: 167,
    description: "Velvet-lined compartments for precious items"
  },
  {
    id: 11,
    name: "Kitchen Pantry System Deluxe",
    price: 799,
    category: "Storage Solutions",
    image: "/api/placeholder/400/400",
    rating: 4.7,
    reviews: 203,
    description: "Clear containers and labeling system for pantry perfection"
  },
  // Home Decor
  {
    id: 12,
    name: "Artisan Ceramic Vase Collection",
    price: 289,
    category: "Home Decor",
    image: "/api/placeholder/400/400",
    rating: 4.8,
    reviews: 124,
    badge: "Handmade",
    description: "Set of three minimalist vases in earth tones"
  },
  {
    id: 13,
    name: "Abstract Metal Wall Sculpture",
    price: 899,
    category: "Home Decor",
    image: "/api/placeholder/400/400",
    rating: 4.9,
    reviews: 78,
    description: "Contemporary bronze finish with geometric design"
  },
  // Professional Tools
  {
    id: 14,
    name: "Professional Label Maker Kit",
    price: 149,
    category: "Professional Tools",
    image: "/api/placeholder/400/400",
    rating: 4.7,
    reviews: 189,
    badge: "Organizer's Choice",
    description: "High-quality labels and tapes used by professionals"
  },
  {
    id: 15,
    name: "Luxury Cleaning Supply Set",
    price: 229,
    category: "Professional Tools",
    image: "/api/placeholder/400/400",
    rating: 4.8,
    reviews: 145,
    description: "Premium eco-friendly cleaning products and tools"
  },
  // Lighting
  {
    id: 16,
    name: "Scandinavian Floor Lamp",
    price: 599,
    category: "Lighting",
    image: "/api/placeholder/400/400",
    rating: 4.9,
    reviews: 156,
    description: "Warm ambient lighting with natural wood base"
  },
  // Textiles
  {
    id: 17,
    name: "Organic Cotton Throw Collection",
    price: 189,
    category: "Textiles",
    image: "/api/placeholder/400/400",
    rating: 4.7,
    reviews: 93,
    description: "Ultra-soft organic cotton in calming earth tones"
  }
];

const Store = () => {
  const [selectedCategory, setSelectedCategory] = useState("All Products");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === "All Products" || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case "price-low":
        return a.price - b.price;
      case "price-high":
        return b.price - a.price;
      case "rating":
        return b.rating - a.rating;
      default:
        return 0;
    }
  });

  return (
    <>
      <SEOHelmet 
        title="Luxury Home Goods Store | Organize by Design Michigan"
        description="Shop curated minimalist furniture, home decor, and organization solutions. Premium quality pieces for modern Michigan homes."
        canonicalUrl="/store"
      />
      
      <div className="min-h-screen bg-background">
        <Header onBookingClick={() => setShowEstimateDialog(true)} />
        
        {/* Hero Section */}
        <section className="relative py-20 px-4 text-center luxury-gradient">
          <div className="container max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-6">
              Curated Luxury Home Goods
            </h1>
            <p className="text-xl text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Minimalist furniture, elegant home decor, and premium organization tools. 
              Handpicked pieces that blend beauty with function for your Michigan home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Badge variant="secondary" className="text-lg px-6 py-2">
                Free shipping on orders over $500
              </Badge>
              <Badge variant="outline" className="text-lg px-6 py-2 bg-white/10 text-primary-foreground border-white/30">
                Curated for modern minimalists
              </Badge>
            </div>
          </div>
        </section>

        {/* Filters & Search */}
        <section className="py-8 px-4 border-b">
          <div className="container max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-48">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Highest Rated</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 px-4">
          <div className="container max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {sortedProducts.map((product) => (
                <Card key={product.id} className="group hover:shadow-lg transition-shadow duration-300">
                  <div className="relative overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {product.badge && (
                      <Badge 
                        className="absolute top-4 left-4 bg-primary text-primary-foreground"
                      >
                        {product.badge}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-4 right-4 bg-white/90 hover:bg-white"
                    >
                      <Heart className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 fill-primary text-primary" />
                        <span className="text-sm font-medium ml-1">{product.rating}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        ({product.reviews} reviews)
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{product.description}</p>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold">${product.price}</span>
                      {product.originalPrice && (
                        <span className="text-lg text-muted-foreground line-through">
                          ${product.originalPrice}
                        </span>
                      )}
                    </div>
                  </CardContent>
                  
                  <CardFooter className="p-6 pt-0">
                    <Button className="w-full luxury-gradient text-primary-foreground">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Add to Cart
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8">Curated for Elevated Living</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4">Minimalist Design</h3>
                <p className="text-muted-foreground">
                  Every piece is selected for its clean lines, quality craftsmanship, and timeless appeal
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Premium Quality</h3>
                <p className="text-muted-foreground">
                  Handpicked luxury furniture and home goods that combine beauty with lasting functionality
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4">Michigan Local Delivery</h3>
                <p className="text-muted-foreground">
                  Fast shipping, white-glove delivery options, and personalized consultation across Michigan
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer onBookingClick={() => setShowEstimateDialog(true)} />
        <EstimateDialog 
          open={showEstimateDialog} 
          onOpenChange={setShowEstimateDialog} 
        />
      </div>
    </>
  );
};

export default Store;